export type IntakePagePlacement = "start" | "end" | "inline";

export type IntakePageDraft = {
  headline: string;
  bullets: string[];
  showTrust: boolean;
  enabled?: boolean;
  placement: IntakePagePlacement;
};

const SELLER_START_BULLETS = [
  "A few short questions. Nothing gets listed.",
  "Your details stay with {First}.",
  "No obligation.",
];

const SELLER_END_BULLETS = [
  "Expect to hear within one business day.",
  "What you shared stays confidential.",
];

const BUYER_START_BULLETS = [
  "No obligation.",
  "Your details stay with {First}.",
  "A few questions, then they follow up.",
];

export function brokerFirstName(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0];
  return first || "your broker";
}

/** Replace the {First} token with the broker’s first name. */
export function fillPageToken(text: string, displayName: string): string {
  return text.replaceAll("{First}", brokerFirstName(displayName));
}

export function defaultStartPage(
  audience: "seller" | "buyer",
): IntakePageDraft {
  if (audience === "buyer") {
    return {
      headline: "A confidential conversation with {First}",
      bullets: [...BUYER_START_BULLETS],
      showTrust: true,
      placement: "start",
    };
  }
  return {
    headline: "A few questions for {First}",
    bullets: [...SELLER_START_BULLETS],
    showTrust: true,
    placement: "start",
  };
}

export function defaultEndPage(audience: "seller" | "buyer"): IntakePageDraft {
  return {
    headline: "{First} will be in touch.",
    bullets:
      audience === "buyer"
        ? [
            "Expect to hear within one business day.",
            "What you shared stays confidential.",
          ]
        : [...SELLER_END_BULLETS],
    showTrust: true,
    placement: "end",
  };
}

export function defaultInlinePage(): IntakePageDraft {
  return {
    headline: "A note before the next question",
    bullets: [],
    showTrust: false,
    placement: "inline",
  };
}

export function pageSettings(draft: IntakePageDraft): Record<string, unknown> {
  return {
    bullets: draft.bullets.slice(0, 3),
    showTrust: draft.showTrust,
    placement: draft.placement,
    ...(draft.enabled === false ? { enabled: false } : {}),
  };
}

export function readPageDraft(input: {
  label: string;
  settings?: unknown;
}): IntakePageDraft {
  const settings =
    input.settings && typeof input.settings === "object"
      ? (input.settings as Record<string, unknown>)
      : {};
  const placement = readPlacement(settings.placement);
  const bullets = Array.isArray(settings.bullets)
    ? settings.bullets
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .slice(0, 3)
    : [];
  return {
    headline: input.label.trim() || "Page",
    bullets,
    showTrust: settings.showTrust === true,
    placement,
    ...(settings.enabled === false ? { enabled: false } : {}),
  };
}

function readPlacement(value: unknown): IntakePagePlacement {
  if (value === "start" || value === "end" || value === "inline") return value;
  return "inline";
}

type PageFieldLike = {
  kind: string;
  fieldKey?: string;
  label?: string;
  settings?: unknown;
};

export function pagePlacementOf(
  field: PageFieldLike,
): IntakePagePlacement | null {
  if (field.kind !== "page") return null;
  return readPageDraft({
    label: field.label ?? "",
    settings: field.settings,
  }).placement;
}

/** Show the default welcome and thank-you pages until the form stores its own. */
export function ensurePinnedPages<TSection extends { fields: PageFieldLike[] }>(
  sections: TSection[],
  audience: "seller" | "buyer",
  createSection: (
    placement: "start" | "end",
    audience: "seller" | "buyer",
  ) => NoInfer<TSection>,
  options?: { includeStart?: boolean },
): TSection[] {
  const includeStart = options?.includeStart ?? true;
  const hasStart = sections.some((section) =>
    section.fields.some((field) => pagePlacementOf(field) === "start"),
  );
  const hasEnd = sections.some((section) =>
    section.fields.some((field) => pagePlacementOf(field) === "end"),
  );
  return [
    ...(includeStart && !hasStart ? [createSection("start", audience)] : []),
    ...sections,
    ...(hasEnd ? [] : [createSection("end", audience)]),
  ];
}

/** Move stored welcome pages ahead of the questions. */
export function hoistStartPages<
  TField extends PageFieldLike,
  TSection extends { fields: TField[] },
>(sections: TSection[], wrap: (fields: TField[]) => TSection): TSection[] {
  const extracted: TField[] = [];
  const rest: TSection[] = [];
  for (const section of sections) {
    const startFields = section.fields.filter(
      (field) => pagePlacementOf(field) === "start",
    );
    const otherFields = section.fields.filter(
      (field) => pagePlacementOf(field) !== "start",
    );
    extracted.push(...startFields);
    if (otherFields.length === 0) continue;
    rest.push(
      otherFields.length === section.fields.length
        ? section
        : { ...section, fields: otherFields },
    );
  }
  if (extracted.length === 0) return sections;
  return [wrap(extracted), ...rest];
}
